import { aUEC } from '@itc-mod/bank/shared';
import { MILLI, fmtCount, fmtSCU } from '@itc-mod/supply/shared';
import { z } from 'zod';
import type { Donation, People } from './schemas.ts';

// The leaderboards (plan 2C R13, Assumption 8; Sierra's `donorStats`,
// sierra-panel.html line 2242). Only credited donors count; reversed and
// unaccepted donations count for nothing, and nor do guests named "Anonymous".
// Several donors each count a donation once, and share its amount and SCU equally,
// the remainder going to the first donor. The items board ranks by value, then by count:
// an unpriced donation is worth 0, so it ranks as it did before prices existed.

/** The key a donor's totals add up under. */
export const pointsKey = (donor: { userId: string | null; name: string | null }) => (donor.userId === null ? `name:${(donor.name ?? '').trim().toLowerCase()}` : `user:${donor.userId}`);

export const BoardPeriod = z.enum(['30', '90', 'all']);
export type BoardPeriod = z.infer<typeof BoardPeriod>;

/** One item total on a donor's row: crates count as SCU of their item. */
export const ItemTotal = z.object({ name: z.string(), unit: z.enum(['scu', 'units', 'kits', 'auec']), quantity: z.number().int() });
export type ItemTotal = z.infer<typeof ItemTotal>;

export const BoardEntry = z.object({
  /** `user:<id>` or `name:<lower-cased name>`. */
  key: z.string(),
  userId: z.string().nullable(),
  name: z.string(),
  /** Donations counted: aUEC ones on the aUEC board, item ones on the items board, all of them for thanks. */
  count: z.number().int(),
  /** The credited aUEC, shared. */
  auec: z.number().int(),
  /** Thousandths of an SCU, shared. */
  scu: z.number().int(),
  /** Hundredths of a point, as fixed at acceptance (plan 4B). */
  value: z.number().int(),
  totals: z.array(ItemTotal),
  /** The newest donation counted. */
  last: z.iso.datetime(),
  /** Opted out of the public boards: hidden from members, marked for receivers. */
  hidden: z.boolean(),
});
export type BoardEntry = z.infer<typeof BoardEntry>;

export const Boards = z.object({ period: BoardPeriod, auec: z.array(BoardEntry), items: z.array(BoardEntry), donors: z.array(BoardEntry) });
export type Boards = z.infer<typeof Boards>;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The day count of a period, or null for all time. */
export function periodDays(period: BoardPeriod): number | null {
  return period === 'all' ? null : Number(period);
}

/** `whole` split `count` ways: equal shares, the remainder to the first. */
export function share(whole: number, count: number, index: number): number {
  const each = Math.floor(whole / count);
  return each + (index === 0 ? whole - each * count : 0);
}

type Tally = BoardEntry & { totalsByKey: Map<string, ItemTotal> };

function rowsOf(tallies: Map<string, Tally>): BoardEntry[] {
  return [...tallies.values()].map(({ totalsByKey, ...entry }) => ({
    ...entry,
    totals: [...totalsByKey.values()].sort((a, b) => Number(a.unit === 'auec') - Number(b.unit === 'auec') || b.quantity - a.quantity),
  }));
}

/** R13: the aUEC board, the items board, and every credited donor for "Copy donor thanks". */
/** What may leave the app (plan 2E R16): those who opted out of the boards are left out, for every viewer. */
export const publicDonors = <T extends Pick<BoardEntry, 'hidden'>>(donors: readonly T[]): T[] => donors.filter((donor) => !donor.hidden);

export function boards(donations: readonly Pick<Donation, 'kind' | 'status' | 'donors' | 'amount' | 'lines' | 'date'>[], options: { period: BoardPeriod; now: Date; people: People; hidden: ReadonlySet<string> }): Boards {
  const days = periodDays(options.period);
  const since = days === null ? Number.NEGATIVE_INFINITY : options.now.getTime() - days * DAY_MS;
  const auec = new Map<string, Tally>();
  const items = new Map<string, Tally>();
  const donors = new Map<string, Tally>();
  const tally = (map: Map<string, Tally>, key: string, base: Omit<BoardEntry, 'totals'>): Tally => {
    let entry = map.get(key);
    if (entry === undefined) {
      entry = { ...base, totals: [], totalsByKey: new Map() };
      map.set(key, entry);
    }
    return entry;
  };
  const addTotal = (entry: Tally, total: ItemTotal) => {
    const key = `${total.unit}|${total.name.toLowerCase()}`;
    const known = entry.totalsByKey.get(key);
    if (known === undefined) entry.totalsByKey.set(key, { ...total });
    else known.quantity += total.quantity;
  };

  for (const donation of donations) {
    if (donation.status !== 'accepted' || Date.parse(donation.date) < since) continue;
    const count = donation.donors.length;
    donation.donors.forEach((donor, index) => {
      if (donor.credit !== 'credited') return;
      const name = donor.name ?? (donor.userId === null ? 'Anonymous' : (options.people[donor.userId] ?? 'a member'));
      if (donor.userId === null && name.trim().toLowerCase() === 'anonymous') return;
      const key = donor.userId === null ? `name:${name.trim().toLowerCase()}` : `user:${donor.userId}`;
      const base = { key, userId: donor.userId, name, count: 0, auec: 0, scu: 0, value: 0, last: donation.date, hidden: donor.userId !== null && options.hidden.has(donor.userId) };
      const entries = [tally(donors, key, base), tally(donation.kind === 'auec' ? auec : items, key, base)];
      for (const entry of entries) {
        entry.count += 1;
        entry.value += donor.value ?? 0;
        if (Date.parse(donation.date) > Date.parse(entry.last)) entry.last = donation.date;
        if (donation.kind === 'auec') {
          const amount = share(donation.amount ?? 0, count, index);
          entry.auec += amount;
          addTotal(entry, { name: '', unit: 'auec', quantity: amount });
          continue;
        }
        for (const line of donation.lines) {
          if (line.quantity <= 0) continue;
          const unit = line.unit === 'crates' ? 'scu' : line.unit;
          const whole = line.unit === 'crates' ? line.quantity * (line.crateSize ?? 0) * MILLI : line.quantity;
          const part = share(whole, count, index);
          if (unit === 'scu') entry.scu += part;
          addTotal(entry, { name: line.name, unit, quantity: part });
        }
      }
    });
  }
  return {
    period: options.period,
    auec: rowsOf(auec).sort((a, b) => b.auec - a.auec || Date.parse(b.last) - Date.parse(a.last)),
    items: rowsOf(items).sort((a, b) => b.value - a.value || b.count - a.count || Date.parse(b.last) - Date.parse(a.last)),
    donors: rowsOf(donors).sort((a, b) => b.count - a.count || Date.parse(b.last) - Date.parse(a.last)),
  };
}

/** `itemTotText`: `5,000,000 aUEC`, `64 SCU RMC`, `4 kits MedGel`, `12 units Nova Rifle`. */
export function totalText(total: ItemTotal): string {
  if (total.unit === 'auec') return aUEC(total.quantity);
  if (total.unit === 'scu') return `${fmtSCU(total.quantity)} SCU ${total.name}`;
  return `${fmtCount(total.quantity)} ${total.unit} ${total.name}`;
}

/** `donorItemsText`: a donor's totals, the first `limit` with "+N more". */
export function totalsText(entry: Pick<BoardEntry, 'totals'>, limit?: number): string {
  const shown = limit === undefined ? entry.totals : entry.totals.slice(0, limit);
  const more = limit !== undefined && entry.totals.length > limit ? ` +${String(entry.totals.length - limit)} more` : '';
  return shown.map(totalText).join(', ') + more;
}
